<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="848"
    role="dialog"
    aria-modal="true"
    aria-labelledby="gg-drawer-site-title"
    class="gg-idrawer"
    @input="$emit('input', $event)"
  >
    <div class="gg-idrawer__header">
      <h2 id="gg-drawer-site-title" class="gg-idrawer__title">Cadastrar novo site</h2>
      <button type="button" class="gg-idrawer__close" aria-label="Fechar" @click="close">
        <v-icon size="24">mdi-close</v-icon>
      </button>
    </div>

    <div class="gg-idrawer__body gg-drawer-form">
      <!-- Identity block — 84 + 16 + 84 + 16 + 56 = the design's 256. -->
      <div class="gg-form-block">
        <div class="gg-field-row">
        <div class="gg-field">
          <label class="gg-field__label" for="gg-site-praca">
            Site<span class="gg-field__required" aria-hidden="true">*</span>
          </label>
          <v-select
            id="gg-site-praca"
            v-model="cod_praca"
            :items="pracas || []"
            item-text="sig_praca"
            item-value="cod_praca"
            placeholder="Selecione"
            solo
            flat
            dense
            hide-details
            append-icon="mdi-menu-down"
            class="gg-field__select"
            :menu-props="{ contentClass: 'gg-select-menu', offsetY: true, nudgeBottom: 4 }"
          />
        </div>
          <div class="gg-field">
            <label class="gg-field__label" for="gg-site-sigla">
              Sigla<span class="gg-field__required" aria-hidden="true">*</span>
            </label>
            <input
              id="gg-site-sigla"
              v-model="sigla"
              type="text"
              class="gg-field__input"
              placeholder="JB"
              required
            />
          </div>
        </div>

        <div class="gg-field">
          <label class="gg-field__label" for="gg-site-nome">
            Nome<span class="gg-field__required" aria-hidden="true">*</span>
          </label>
          <input
            id="gg-site-nome"
            v-model="nome"
            type="text"
            class="gg-field__input"
            placeholder="Rio de Janeiro"
            required
          />
        </div>

        <div class="gg-switch-field">
          <v-switch v-model="active" label="Ativo" hide-details dense class="gg-switch mt-0 pt-0" />
        </div>
      </div>

      <!-- MSE block — 24 + 24 + 84 + 24 + (84 + 40) = the design's 280. -->
      <div class="gg-form-block gg-form-block--loose">
        <h3 class="gg-form-section">Servidor MSE</h3>

        <div class="gg-field">
          <label class="gg-field__label" for="gg-site-mse-url">
            URL<span class="gg-field__required" aria-hidden="true">*</span>
          </label>
          <input
            id="gg-site-mse-url"
            v-model="baseUrl"
            type="text"
            class="gg-field__input"
            placeholder="http://host:8580"
            required
          />
        </div>

        <!-- Field and its action are flush in the design, so this pair carries no gap. -->
        <div class="gg-form-guid">
          <div class="gg-field">
            <label class="gg-field__label" for="gg-site-show-guid">Template show guid</label>
            <input
              id="gg-site-show-guid"
              :value="templateShowGuid"
              type="text"
              class="gg-field__input"
              placeholder="Gerado ao provisionar a template show"
              readonly
            />
          </div>
          <!-- Provisioning creates the show on the MSE and pins its GUID, so it can
               only run once the site row exists to attach it to. -->
          <button type="button" class="gg-inline-action" disabled>
            <v-icon>mdi-plus-box-outline</v-icon>
            Provisionar
          </button>
          <span class="gg-field__hint">Salve o site para provisionar a template show.</span>
        </div>
      </div>
    </div>

    <div class="gg-idrawer__footer">
      <button type="button" class="gg-idrawer__submit" :disabled="!canSave" @click="save">
        {{ saving ? 'Salvando…' : 'Salvar' }}
      </button>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ggDrawerA11y from "@/mixins/ggDrawerA11y";

export default {
  name: "GloboGraphicsSiteFormDrawer",
  mixins: [ggDrawerA11y],
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      sigla: "",
      nome: "",
      cod_praca: null,
      active: true,
      baseUrl: "",
      // Provisioning-managed and never user-editable; shown so the drawer matches
      // the shape of the site it is creating.
      templateShowGuid: "",
      saving: false,
    };
  },
  computed: {
    ...mapGetters("pracas", { pracas: "getterPracas" }),
    canSave() {
      return !!(this.sigla.trim() && this.nome.trim() && this.cod_praca && this.baseUrl.trim() && !this.saving);
    },
  },
  watch: {
    // The drawer stays mounted across open/close, so seed on every open —
    // otherwise a cancelled draft reappears next time.
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    ...mapActions("sites_admin", ["createSite"]),
    reset() {
      this.sigla = "";
      this.nome = "";
      this.cod_praca = null;
      this.active = true;
      this.baseUrl = "";
      this.templateShowGuid = "";
    },
    close() {
      this.$emit("input", false);
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        const sigla = this.sigla.trim();
        await this.createSite({
          sigla,
          nome: this.nome.trim(),
          active: this.active,
          cod_praca: this.cod_praca,
          // Only the base URL is user-editable; the GUID is provisioning-managed
          // and the naming is standardized server-side.
          mse: { baseUrl: this.baseUrl.trim() },
        });
        this.$emit("created", sigla);
        this.$toast?.success?.("Site criado");
        this.close();
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.error || "Erro ao criar site");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Drawer chrome comes from _gg-import-drawer.scss and the fields/buttons from
// _gg-dialog.scss; only the form rhythm and the guid hint are local.
// Form chrome (.gg-drawer-form / .gg-form-block) comes from _gg-dialog.scss.
.gg-form-guid {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.gg-field__hint {
  color: var(--sc-gg-text-tertiary);
  font-size: 14px;
  line-height: 20px;
}
</style>
